import { formatAuthError } from "../../localization/authErrors";
import { translate, useLocale } from "../../localization";
import React, { useState } from "react";
import { Alert, Box } from "@mui/material";
import AuthForm from "../../components/AuthForm"; // Reusable form component
import {
  requestRecoveryCode,
  verifyRecoveryEmail,
  finishPasswordRecovery,
} from "../../services/passwordRecovery";
import { AppButton } from "../../components/ui";
import { useNavigate } from "react-router-dom";
import Page from "../../components/Page";

const normalizeEmail = (value) => value.trim().toLowerCase();

const ResetPasswordPage = () => {
  useLocale();
  const [email, setEmail] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState("request");
  const [notice, setNotice] = useState("");
  const navigate = useNavigate();

  const handleResetRequest = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const nextStep = await requestRecoveryCode(normalizeEmail(email));
      setStep(nextStep);
      setConfirmationCode("");
      setNotice(nextStep === "verify"
        ? translate("We sent a fresh email confirmation code. Enter it here, then we\u2019ll send your password reset code.")
        : translate("We sent a fresh password reset code. Check your inbox and spam folder."));
    } catch (err) {
      console.error("Error during reset password request:", err);
      setError(formatAuthError(err, translate("An unexpected error occurred.")));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailVerification = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await verifyRecoveryEmail(email, confirmationCode);
      // Clear the consumed code before sending the reset email. If delivery
      // fails, the user can request it again without re-confirming their email.
      setStep("request");
      setConfirmationCode("");
      setNotice(translate("Email confirmed. Request a password reset code to continue."));
      const nextStep = await requestRecoveryCode(email);
      setStep(nextStep);
      setNotice(translate("Email confirmed. We sent a new password reset code. Enter the new code below."));
    } catch (err) {
      setError(formatAuthError(err, translate("Unable to verify your email.")));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetConfirmation = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await finishPasswordRecovery(email, confirmationCode, newPassword);
      console.log("Password reset successfully.");
      navigate("/login"); // Redirect to login page after successful reset
    } catch (err) {
      console.error("Error during password reset confirmation:", err);
      setError(formatAuthError(err, translate("An unexpected error occurred.")));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Page>
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        textAlign="center"
        alignItems="center"
        justifyContent="center"
        padding={20}
      >
        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}
        {notice && <Alert severity="info">{notice}</Alert>}
        {step === "request" ? (
          <AuthForm
            title={translate("Reset Password")}
            fields={[
              {
                label: translate("Email"),
                type: "text",
                value: email,
                onChange: (e) => setEmail(e.target.value),
              },
            ]}
            onSubmit={handleResetRequest}
            isLoading={isLoading}
            footerAction={
              <Alert severity="info" sx={{ marginTop: 2 }}>{translate("Enter your email to receive a password reset code.")}</Alert>
            }
          />
        ) : (
          <AuthForm
            title={step === "verify" ? translate("Verify Email") : translate("Confirm Password Reset")}
            fields={[
              {
                label: translate("Verification Code"),
                type: "text",
                value: confirmationCode,
                onChange: (e) => setConfirmationCode(e.target.value),
              },
              ...(step === "reset" ? [{
                label: translate("New Password"),
                type: "password",
                value: newPassword,
                onChange: (e) => setNewPassword(e.target.value),
              }] : []),
            ]}
            onSubmit={step === "verify" ? handleEmailVerification : handleResetConfirmation}
            isLoading={isLoading}
            footerAction={
              <Box>
                <AppButton kind="ghost" disabled={isLoading} onClick={handleResetRequest}>{translate("Resend code")}</AppButton>
                <AppButton kind="ghost" disabled={isLoading} onClick={() => {
                  setStep("request");
                  setConfirmationCode("");
                  setNewPassword("");
                  setError(null);
                  setNotice("");
                }}>{translate("Use a different email")}</AppButton>
              </Box>
            }
          />
        )}
      </Box>
    </Page>
  );
};

export default ResetPasswordPage;
